import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { supabase } from '../../../lib/supabase'
import CustomConfirmModal from '../../common/CustomConfirmModal'

type NotifyType = 'success' | 'error' | 'warning' | 'info' | 'danger'

interface DeleteAccountSectionProps {
    showNotify: (title: string, message: string, type: NotifyType, callback?: () => void) => void
    isLoading: boolean
}

export default function DeleteAccountSection({ showNotify, isLoading: globalLoading }: Readonly<DeleteAccountSectionProps>) {
    const { t } = useTranslation()
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
    const [showVerificationConfirm, setShowVerificationConfirm] = useState(false)
    const [isDeleting, setIsDeleting] = useState(false)

    const handleDeleteAccount = () => {
        setShowDeleteConfirm(true)
    }

    const confirmDeleteAccount = () => {
        setShowDeleteConfirm(false)
        setShowVerificationConfirm(true)
    }

    const executeDeleteAccount = async (verification?: string) => {
        if (verification !== 'BORRAR') {
            showNotify(t('settings.danger.notifications.unmatchedTitle'), t('settings.danger.notifications.unmatched'), 'warning')
            setShowVerificationConfirm(false)
            return
        }

        try {
            setIsDeleting(true)
            setShowVerificationConfirm(false)

            // Call the Edge Function to delete the account
            const { error } = await supabase.functions.invoke('delete-account')

            if (error) throw error

            // Sign out (user no longer exists)
            await supabase.auth.signOut()

            showNotify(t('settings.danger.notifications.deletedTitle'), t('settings.danger.notifications.deleted'), 'info', () => {
                window.location.href = '/' // Redirect to landing
            })
        } catch (error) {
            console.error('Error during account deletion:', error)
            showNotify('Error', t('settings.danger.notifications.error', { error: (error as Error).message }), 'danger')
            setIsDeleting(false)
        }
    }

    const loading = globalLoading || isDeleting

    return (
        <>
            <section className="settings-section danger-zone">
                <div className="section-header">
                    <div className="section-icon">⚠️</div>
                    <div>
                        <h3 className="section-title">{t('settings.danger.title')}</h3>
                        <p className="section-desc">{t('settings.danger.subtitle')}</p>
                    </div>
                </div>

                <div className="danger-content">
                    <p style={{ marginBottom: '1rem', color: '#7f1d1d' }}>
                        {t('settings.danger.warning')}
                    </p>
                    <button onClick={handleDeleteAccount} className="btn btn-danger" disabled={loading}>
                        {t('settings.danger.deleteBtn')}
                    </button>
                </div>
            </section>

            <CustomConfirmModal
                isOpen={showDeleteConfirm}
                type="danger"
                title={t('settings.danger.modal1.title')}
                message={t('settings.danger.modal1.message')}
                confirmText={t('settings.danger.modal1.confirm')}
                onConfirm={confirmDeleteAccount}
                onCancel={() => setShowDeleteConfirm(false)}
            />

            <CustomConfirmModal
                isOpen={showVerificationConfirm}
                type="danger"
                title={t('settings.danger.modal2.title')}
                message={t('settings.danger.modal2.message')}
                showInput={true}
                inputPlaceholder={t('settings.danger.modal2.placeholder')}
                confirmText={t('settings.danger.modal2.confirm')}
                onConfirm={executeDeleteAccount}
                onCancel={() => setShowVerificationConfirm(false)}
            />
        </>
    )
}
